/**
 * Container for the FullScreenOverlay component
 *
 * Provides safe area and screen-size context to the view layer.
 * On mobile (useMobileScreen), forces full-screen rendering.
 * On desktop, uses configurable size. Theme is handled by the view's
 * semantic tokens (which follow the active theme), so no theme prop is needed.
 */
import { ReactNode } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import FullScreenOverlayView from "./FullScreenOverlayView";

import useMobileScreen from "@/hooks/useMobileScreen";

/**
 * Props for FullScreenOverlayContainer
 */
interface FullScreenOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  desktopSize?: "xs" | "sm" | "md" | "lg" | "xl" | "full";
  headerContent?: ReactNode;
}

/**
 * FullScreenOverlayContainer provides safe area and screen-size context
 * @param props - Container props
 * @param props.isOpen - Whether the overlay is visible
 * @param props.onClose - Callback to close the overlay
 * @param props.title - Header title text
 * @param props.children - Content to render inside the overlay
 * @param props.desktopSize - Modal size on desktop (default "lg")
 * @param props.headerContent - Additional content in the header row
 * @returns FullScreenOverlayView with computed layout props
 */
const FullScreenOverlayContainer = ({
  isOpen,
  onClose,
  title,
  children,
  desktopSize = "lg",
  headerContent,
}: FullScreenOverlayProps) => {
  const insets = useSafeAreaInsets();
  const isSmallScreen = useMobileScreen();

  return (
    <FullScreenOverlayView
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      topInset={insets.top}
      isSmallScreen={isSmallScreen}
      desktopSize={desktopSize}
      headerContent={headerContent}
    >
      {children}
    </FullScreenOverlayView>
  );
};

export default FullScreenOverlayContainer;
